import type { AppLocale } from './locale';

/**
 * AI 協助填寫(assist)— docs/76 §3(ADR-035 雙色卡)
 * -----------------------------------------------------------------------------
 * `POST /api/projects/{pid}/nodes/{nid}/assist` — 每張綠卡的唯一 AI 填寫入口
 * (BE 側 = ideation 模組改造;橘卡 ideation 端點退場,見 docs/76 §10)。
 *
 * 兩情境 BE 自動分流(FE 不帶參數):卡上有料 → 潤飾補全;接在藍卡後空白卡 →
 * 讀上游產物延續創意。
 *
 * **計費(UI 文案紅線)**:凡真打 LLM 一律收費 — 不論 verdict(含 ASK 回提問);
 * 純結構缺料(必填空/模板素材缺)BE 不打 LLM 直接 ASK,`charged_credits: 0` 免費。
 * 面板常駐 hint:「AI 協助填寫將消耗點數(包含 AI 回覆提問的情況)」。
 * 限流 10/min(429)。
 *
 * **寫回**:PROCEED 的 proposal 由 FE 給用戶預覽(可改)→ 確認後走既有
 * `PATCH /nodes/{nid}`(整包覆寫 + OCC)— 後端不直寫卡(單一寫者不變量)。
 * 所有 BE 回傳字串(proposal / questions / reason)一律 JSX text 渲染,禁 innerHTML。
 */

export type AssistVerdict = 'PROCEED' | 'ASK' | 'CLARIFY' | 'REFUSE';

// ─── DTO(snake_case wire)─────────────────────────────────────────────────────

export interface AssistRequestDto {
  /**
   * 建議帶(ULID)。**v0.3 規則**:每次「新的按下」都要鑄新 key;同 key 只 dedup 同一次
   * 按下的網路 retry / 雙擊。ASK 後補答(改了 text_value/slots/config)再用**舊 key**
   * → 409 `IDEMPOTENCY_KEY_CONFLICT`(fail-loud,不會靜默回舊 ASK)→ 換新 key 重打。
   * (AssistSection 每次 `runAssist` 呼 `newIdempotencyKey()`,天然滿足。)
   */
  idempotency_key?: string;
  /**
   * R424(BE docs/137 §1)— 使用者當下的介面語系;AI 回覆(提案 / 提問 / 原因)用這個語系寫。
   * **只在 catalog `features.output_locale` 時帶**(舊 BE `extra="forbid"` → 422);
   * 值一律由 `features/canvas/outputLocale.currentOutputLocale()` 取,不在呼叫點自己轉。
   */
  locale?: AppLocale;
}

export interface AssistProposalDto {
  /**
   * 填好的業務欄(整包供 PATCH 覆寫預覽)。
   *
   * R170 — **不對內容欄做假設**:`execution_style` 隨 ADR-040 W1 退場、`creative_type`
   * 隨 W3 從 request 移除(BE 是否仍在 proposal 回它未定義)。這裡是 passthrough
   * `Record<string, unknown>`,套用時整包 PATCH;`nodeConfigKeys` 的 retired 組會在
   * persist/submit 前把退場欄剝掉,所以 BE 多回什麼都不會污染節點。
   */
  config: Record<string, unknown>;
  /** 潤飾後的需求描述(綠卡頂層 text_value)。 */
  text_value: string;
}

export interface AssistResultDto {
  verdict: AssistVerdict;
  /** 僅 PROCEED。 */
  proposal?: AssistProposalDto | null;
  /** ASK / CLARIFY 的引導提問。 */
  questions?: string[] | null;
  /** ASK 的缺料清單(如「產品參考圖」)。 */
  missing_assets?: string[] | null;
  /** REFUSE / CLARIFY 的說明(REFUSE 帶合規 advisory;沿 docs/55 語意)。 */
  reason?: string | null;
  /** 一律有:0 = 純結構缺料(未打 LLM);>0 = 已扣點(不論 verdict)。 */
  charged_credits: number;
}

// ─── domain(camelCase)────────────────────────────────────────────────────────

export interface AssistProposal {
  config: Record<string, unknown>;
  textValue: string;
}

export interface AssistResult {
  verdict: AssistVerdict;
  proposal: AssistProposal | null;
  questions: string[];
  missingAssets: string[];
  reason: string | null;
  chargedCredits: number;
}

// ─── mapper ──────────────────────────────────────────────────────────────────

export function mapAssistResultDto(dto: AssistResultDto): AssistResult {
  return {
    verdict: dto.verdict,
    proposal: dto.proposal
      ? { config: dto.proposal.config ?? {}, textValue: dto.proposal.text_value ?? '' }
      : null,
    questions: dto.questions ?? [],
    missingAssets: dto.missing_assets ?? [],
    reason: dto.reason ?? null,
    chargedCredits: dto.charged_credits,
  };
}
